import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { firstAllowedPath } from '../utils/rbacDefaultRoute';
import { canAccessSecurityAdmin } from '../utils/rbacSecurityAccess';
import { userAuditApi, UserAuditEventDTO } from '../services/userAuditApi';
import { showError } from '../utils/notifications';
import './UsersPage.css';
import './UserAuditPage.css';

const EVENT_LABELS: Record<string, string> = {
  AUTH_LOGIN: 'Inicio de sesión',
  AUTH_LOGIN_FAILED: 'Fallo de inicio de sesión',
  CAMERA_STREAM_VIEW: 'Streaming — visualización de cámara',
  RECORDING_PLAYBACK_VIEW: 'Grabaciones — reproducción'
};

function labelEventType(type: string): string {
  return EVENT_LABELS[type] ?? type;
}

function formatWhen(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('es-ES', {
    dateStyle: 'short',
    timeStyle: 'medium'
  });
}

function formatPayload(p: Record<string, unknown> | null): string {
  if (!p || Object.keys(p).length === 0) return '—';
  try {
    const s = JSON.stringify(p);
    return s.length > 140 ? `${s.slice(0, 137)}…` : s;
  } catch {
    return '—';
  }
}

function formatDurationSeconds(s: number | null | undefined): string {
  if (s == null || !Number.isFinite(s)) return '—';
  const sec = Math.max(0, Math.floor(s));
  if (sec < 60) return `${sec} s`;
  const m = Math.floor(sec / 60);
  const r = sec % 60;
  if (m < 60) return `${m}m ${r}s`;
  const h = Math.floor(m / 60);
  const m2 = m % 60;
  return `${h}h ${m2}m`;
}

function successCell(v: boolean | null): React.ReactNode {
  if (v === true) return <span className="audit-pill audit-pill--ok">Sí</span>;
  if (v === false) return <span className="audit-pill audit-pill--fail">No</span>;
  return <span className="users-muted">—</span>;
}

function defaultDateRange(): { from: string; to: string } {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 7);
  const toStr = to.toISOString().slice(0, 10);
  const fromStr = from.toISOString().slice(0, 10);
  return { from: fromStr, to: toStr };
}

interface AppliedFilters {
  range: { from: string; to: string };
  eventType: string;
  actorUserId: string;
}

function rangeToIso(range: { from: string; to: string }): { from?: string; to?: string } {
  let from: string | undefined;
  let to: string | undefined;
  if (range.from) {
    const d = new Date(`${range.from}T00:00:00`);
    if (!Number.isNaN(d.getTime())) from = d.toISOString();
  }
  if (range.to) {
    const d = new Date(`${range.to}T23:59:59.999`);
    if (!Number.isNaN(d.getTime())) to = d.toISOString();
  }
  return { from, to };
}

const UserAuditPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const initialRange = useMemo(() => defaultDateRange(), []);
  const [draftRange, setDraftRange] = useState(initialRange);
  const [draftEventType, setDraftEventType] = useState('');
  const [draftActorUserId, setDraftActorUserId] = useState('');
  const [applied, setApplied] = useState<AppliedFilters>(() => ({
    range: initialRange,
    eventType: '',
    actorUserId: ''
  }));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [rows, setRows] = useState<UserAuditEventDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const appliedIso = useMemo(() => rangeToIso(applied.range), [applied.range]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const actor =
        applied.actorUserId.trim() === ''
          ? undefined
          : parseInt(applied.actorUserId.trim(), 10);
      const res = await userAuditApi.list({
        from: appliedIso.from,
        to: appliedIso.to,
        eventType: applied.eventType || undefined,
        actorUserId: actor != null && !Number.isNaN(actor) ? actor : undefined,
        page,
        pageSize
      });
      setRows(res.rows);
      setTotal(res.total);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: { message?: string } } }; message?: string };
      showError(
        err.response?.data?.error?.message || err.message || 'No se pudo cargar la auditoría'
      );
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [applied, appliedIso.from, appliedIso.to, page, pageSize]);

  useEffect(() => {
    if (user && !canAccessSecurityAdmin(user)) {
      navigate(firstAllowedPath(user.allowedModules, false, user.allowedActions), {
        replace: true
      });
    }
  }, [user, navigate]);

  useEffect(() => {
    if (user && canAccessSecurityAdmin(user)) {
      void load();
    }
  }, [user, load]);

  if (user && !canAccessSecurityAdmin(user)) {
    return null;
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const applyDraft = () => {
    setApplied({
      range: { ...draftRange },
      eventType: draftEventType,
      actorUserId: draftActorUserId
    });
    setPage(1);
  };

  return (
    <div className="users-page audit-page">
      <div className="users-header">
        <div className="users-header-row">
          <div>
            <h1>
              <ClipboardList size={28} className="users-title-icon" aria-hidden />
              Auditoría
            </h1>
            <p className="users-subtitle">
              Registro de acciones del sistema (solo super administrador).
            </p>
          </div>
          <button type="button" className="users-btn-primary audit-refresh" onClick={() => void load()}>
            <RefreshCw size={18} aria-hidden />
            Actualizar
          </button>
        </div>
      </div>

      <div className="audit-filters">
        <label className="audit-filter">
          <span>Desde</span>
          <input
            type="date"
            value={draftRange.from}
            onChange={(e) => setDraftRange((r) => ({ ...r, from: e.target.value }))}
          />
        </label>
        <label className="audit-filter">
          <span>Hasta</span>
          <input
            type="date"
            value={draftRange.to}
            onChange={(e) => setDraftRange((r) => ({ ...r, to: e.target.value }))}
          />
        </label>
        <label className="audit-filter">
          <span>Tipo de evento</span>
          <select value={draftEventType} onChange={(e) => setDraftEventType(e.target.value)}>
            <option value="">Todos</option>
            <option value="AUTH_LOGIN">Inicio de sesión</option>
            <option value="AUTH_LOGIN_FAILED">Fallo de inicio de sesión</option>
            <option value="CAMERA_STREAM_VIEW">Streaming (cámara)</option>
            <option value="RECORDING_PLAYBACK_VIEW">Grabaciones (reproducción)</option>
          </select>
        </label>
        <label className="audit-filter">
          <span>ID usuario actor</span>
          <input
            type="text"
            inputMode="numeric"
            placeholder="Opcional"
            value={draftActorUserId}
            onChange={(e) => setDraftActorUserId(e.target.value)}
          />
        </label>
        <label className="audit-filter">
          <span>Por página</span>
          <select
            value={String(pageSize)}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </label>
        <button type="button" className="audit-filter-apply users-btn-primary" onClick={applyDraft}>
          Aplicar filtros
        </button>
      </div>

      <div className="users-table-container">
        <div className="users-table-header">
          <h2>Eventos ({total})</h2>
          <p className="audit-page-hint">
            Página {page} de {totalPages}
          </p>
        </div>
        {loading ? (
          <p className="users-table-loading">Cargando…</p>
        ) : (
          <div className="audit-table-wrap">
            <table className="users-table audit-table">
              <thead>
                <tr>
                  <th>Fecha / hora</th>
                  <th>Evento</th>
                  <th>Duración</th>
                  <th>Sitio</th>
                  <th>Cámara</th>
                  <th>Éxito</th>
                  <th>Motivo</th>
                  <th>Actor (email)</th>
                  <th>ID actor</th>
                  <th>IP</th>
                  <th>Detalles (JSON)</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="users-muted audit-empty">
                      No hay eventos en el rango seleccionado.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.id}>
                      <td className="audit-cell-nowrap">{formatWhen(r.occurredAt)}</td>
                      <td>{labelEventType(r.eventType)}</td>
                      <td className="audit-cell-nowrap">{formatDurationSeconds(r.durationSeconds)}</td>
                      <td>{r.siteId ?? '—'}</td>
                      <td>{r.cameraId ?? '—'}</td>
                      <td>{successCell(r.success)}</td>
                      <td>{r.reason ?? '—'}</td>
                      <td className="audit-cell-email">{r.actorEmail ?? '—'}</td>
                      <td>{r.actorUserId ?? '—'}</td>
                      <td className="audit-cell-mono">{r.ipAddress ?? '—'}</td>
                      <td>
                        <span className="audit-payload" title={r.payload ? JSON.stringify(r.payload) : ''}>
                          {formatPayload(r.payload)}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
        {!loading && total > 0 ? (
          <div className="audit-pagination">
            <button
              type="button"
              className="audit-page-btn"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              aria-label="Página anterior"
            >
              <ChevronLeft size={20} />
            </button>
            <span className="audit-page-info">
              {page} / {totalPages}
            </span>
            <button
              type="button"
              className="audit-page-btn"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              aria-label="Página siguiente"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default UserAuditPage;
